import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Anime } from '@/mocks/anime';

interface AnimeListProps {
  animeList: Anime[];
  categoryName: string;
}

export default function AnimeList({ animeList, categoryName }: AnimeListProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [sortBy, setSortBy] = useState('latest');
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredAnime = animeList
    .filter((a) => filterRegion === 'all' || a.region === filterRegion)
    .filter((a) => filterStatus === 'all' || a.status === filterStatus);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -320 : 320,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="py-10 md:py-14 bg-background-50">
      <div className="px-4 md:px-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
              {t('全部番剧')}
            </h2>
            <p className="text-sm text-foreground-500 mt-1">
              {t('共')} {filteredAnime.length} {t('部')}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center bg-background-200 rounded-full px-1 py-1">
              {(['all', '国产', '日本'] as const).map((region) => (
                <button
                  key={region}
                  onClick={() => setFilterRegion(region)}
                  className={`text-xs px-3 py-1.5 rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    filterRegion === region
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {region === 'all' ? t('全部') : region}
                </button>
              ))}
            </div>

            <div className="flex items-center bg-background-200 rounded-full px-1 py-1">
              {(['all', '已完结', '连载中'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`text-xs px-3 py-1.5 rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    filterStatus === status
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {status === 'all' ? t('全部') : status}
                </button>
              ))}
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-xs bg-background-200 text-foreground-700 px-3 py-1.5 rounded-full border-0 outline-none cursor-pointer"
            >
              <option value="latest">{t('最新更新')}</option>
              <option value="rating">{t('评分最高')}</option>
              <option value="popular">{t('最受欢迎')}</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => scroll('left')}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-500 hover:text-white text-foreground-600 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <button
            onClick={() => scroll('right')}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-500 hover:text-white text-foreground-600 transition-all cursor-pointer"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {filteredAnime.map((anime) => (
            <Link
              key={anime.id}
              to={`/anime/${anime.id}`}
              className="flex-shrink-0 w-[220px] md:w-[260px] group cursor-pointer"
            >
              <div className="relative rounded-lg overflow-hidden aspect-[3/4]">
                <img
                  src={anime.cover}
                  alt={anime.title}
                  title={`${anime.title} - 樱花动漫`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute top-2 right-2 z-10">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                      anime.status === '已完结'
                        ? 'bg-background-50/80 text-foreground-700'
                        : 'bg-primary-500/90 text-white'
                    }`}
                  >
                    {anime.status}
                  </span>
                </div>
                <div className="absolute top-2 left-2 z-10">
                  <span className="bg-accent-500/90 text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                    ★ {anime.rating}
                  </span>
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-12">
                  <h3 className="text-white font-medium text-sm md:text-base mb-1 whitespace-nowrap overflow-hidden text-ellipsis">
                    {anime.title}
                  </h3>
                  <p className="text-white/70 text-xs">{anime.episodes}{t('话')} | {anime.region}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}